import React from 'react';
import debounce from 'lodash/debounce';
import Rollbar from 'rollbar';
import {Context, getRollbarFromContext} from '@rollbar/react';
import ErrorReloadPage from '../../../components/ErrorReloadPage';

import './index.scss';

type propType = typeof RerenderOnError.defaultProps & {
  errorMaxRerenders?: number;
  errorWaitReset?: number;
  children?: React.ReactNode;
};
type stateType = {
  error?: Error;
  currentErrorRerenders?: number;
  isPermanentlyFailed?: boolean;
};

class RerenderOnError extends React.Component<propType, stateType> {
  static defaultProps = {
    errorMaxRerenders: 10,
    errorWaitReset: 1000,
    children: <></>
  };

  static contextType = Context;

  rollbar: Rollbar | undefined;

  constructor(props: propType) {
    super(props);
    this.state = {
      error: null,
      currentErrorRerenders: 0,
      isPermanentlyFailed: false
    };
  }

  componentDidMount(): void {
    this.rollbar = getRollbarFromContext(this.context);
  }

  componentDidCatch(error: Error): void {
    const {errorMaxRerenders} = this.props;
    const {currentErrorRerenders} = this.state;

    this.rollbar.error(error);
    // increment current error rerenders
    this.setState({
      currentErrorRerenders: currentErrorRerenders + 1,
      isPermanentlyFailed: currentErrorRerenders + 1 > errorMaxRerenders
    });
    // reset currentErrors after certain amount of time
    this.debouncedResetCurrentErrorRerenders();
  }

  static getDerivedStateFromError = (error: Error): stateType => {
    return {error};
  };

  resetCurrentErrorRerender = (): void => {
    this.setState({
      currentErrorRerenders: 0
    });
  };

  // eslint-disable-next-line react/sort-comp
  debouncedResetCurrentErrorRerenders = debounce(
    this.resetCurrentErrorRerender,
    // eslint-disable-next-line react/destructuring-assignment
    this.props.errorWaitReset || 1000
  );

  renderErrorMessage = (): React.ReactNode => (
    <div className="error-message">
      <div>Error</div>
      <div>Check console for stacktrace</div>
    </div>
  );

  renderApp = (): React.ReactNode => {
    const {isPermanentlyFailed} = this.state;
    const {children} = this.props;
    if (!isPermanentlyFailed) {
      return <>{children}</>;
    }
    return <ErrorReloadPage />;
  };

  render = (): React.ReactNode => {
    const {error} = this.state;
    const showError = __DEVELOPMENT__ && error;
    return showError ? this.renderErrorMessage() : this.renderApp();
  };
}

export default RerenderOnError;
